import { css } from '@emotion/react';
import { noop } from 'lodash';
import { memo } from 'react';

import type { McqQuestion } from '@didaskill/domain/assignments/types';
import { Sortable, useSortableList } from '@didaskill/ui/design-system';

import { QuestionItem, type QuestionItemProps } from './question-item/QuestionItem';

export type QuestionsListProps = {
  questions: McqQuestion[];
  onChangeOrder?: (newOrder: McqQuestion[]) => void;
  onEditQuestion?: QuestionItemProps['onEditQuestion'];
  onRemoveQuestion?: QuestionItemProps['onRemoveQuestion'];
  readOnly?: boolean;
  isLoading?: boolean;
};

const DEFAULT_QUESTIONS = [];

export const QuestionsListBase = ({
  questions = DEFAULT_QUESTIONS,
  onChangeOrder = noop,
  onEditQuestion = noop,
  onRemoveQuestion = noop,
  readOnly = false,
  isLoading = false,
}: QuestionsListProps) => {
  const [draggableQuestions, onChangeDraggableQuestions, onDrop] = useSortableList(
    questions,
    onChangeOrder,
  );

  return (
    <Sortable
      list={draggableQuestions}
      setList={onChangeDraggableQuestions}
      onEnd={onDrop}
      handle={`.${QuestionItem.DRAGGABLE_CLASSNAME}`}
      disabled={readOnly || isLoading}
      css={css`
        display: flex;
        flex-direction: column;
        gap: 10px;
      `}
    >
      {draggableQuestions.map(({ data: question }) => (
        <QuestionItem
          key={question.id}
          question={question}
          isDraggable={!readOnly && questions.length > 1}
          onEditQuestion={onEditQuestion}
          onRemoveQuestion={onRemoveQuestion}
          readOnly={readOnly}
        />
      ))}
    </Sortable>
  );
};

QuestionsListBase.displayName = 'QuestionsList';

export const QuestionsList = memo(QuestionsListBase);
